import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { getTracker } from '@/telemetry/tracker';
import { eventsModule } from '@/events/events-module';
import { markInteractionRequired } from '@/auth/interaction-required';
import { getRouterInstance } from '@/router/router-instance';
import { postFavoriteToggle } from './broadcast';
import { useClientStore, type ClientInfo } from './client-store';

/**
 * One-shot guard: a zero-client bounce replaces history to /login (a page
 * navigation), so it must fire at most once even under React StrictMode's
 * double-invoked effects or a re-render before the redirect commits.
 */
let zeroClientRedirectInitiated = false;

/**
 * One-shot guard for a client-list FETCH FAILURE bounce to /login. Reset on the
 * next successful fetch so a later session can re-trigger it.
 */
let clientFetchFailureHandled = false;

interface RumClientsResponse {
  clients: Array<{ guid: string; name: string | null; isFavorite: boolean }>;
}

/** React Query key for the user's client list. */
export const CLIENTS_QUERY_KEY = ['clients'] as const;

/**
 * Fetch + normalise the user's clients from RUM. Shared by {@link useClients}
 * (store hydration) and the post-login `FrameRedirectPage` (pre-shell client
 * resolution — legacy `getClientsFromRum`). Mirrors
 * `HeaderService.getAllUserClients()`.
 */
export async function fetchUserClients(): Promise<ClientInfo[]> {
  const data = await http<RumClientsResponse>(Endpoints.rumUserClients());
  return data.clients
    .filter((c) => c.name != null)
    .map((c) => ({ id: c.guid, name: c.name as string, isFavorite: c.isFavorite }));
}

import { CLIENT_STORAGE_KEYS } from './client-store';

/**
 * Read the active client id from storage. localStorage only, matching the
 * legacy Angular shell (client identity is never session-scoped) so all tabs,
 * MFEs and the Angular shell resolve the same client.
 */
function readSavedClientId(): string | null {
  return localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
}

/**
 * Loads the user's clients from RUM and syncs them into the Zustand store.
 * Returned clients are also exposed via the SDK `client` module.
 *
 * Mirrors `HeaderService.getAllUserClients()` from the legacy Angular header.
 */
export function useClients() {
  const setAvailable = useClientStore((s) => s.setAvailable);
  const setCurrent = useClientStore((s) => s.setCurrent);
  const current = useClientStore((s) => s.current);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: fetchUserClients,
    staleTime: 5 * 60_000,
  });

  // Legacy `EY_RAPToR_refreshClients(force)` bridge — bound by the M2.9 shim
  // to emit `shell:refresh-clients`. `force=true` bypasses staleTime by
  // invalidating; otherwise we just refetch.
  useEffect(() => {
    return eventsModule.on<{ force: boolean }>('shell:refresh-clients', (payload) => {
      if (payload?.force) {
        void queryClient.invalidateQueries({ queryKey: ['clients'] });
      } else {
        void queryClient.refetchQueries({ queryKey: ['clients'] });
      }
    });
  }, [queryClient]);

  // Hydrate store + select/validate the active client whenever the list arrives
  // or refetches.
  useEffect(() => {
    if (!query.data) return;
    setAvailable(query.data);

    // Angular parity (`getClientsFromRum` / `getSelectedClientName`): the
    // dropdown value is always resolved against the CURRENT list. Keep `current`
    // only while it still exists in the fresh list; if a refetch (SignalR
    // rumupdate, staleTime, EY_RAPToR_refreshClients) drops it, fall back to the
    // stored id or clients[0] so `current` never points outside `available`
    // (which makes MotifSelect log "value not found in available options").
    if (current && query.data.some((c) => c.id === current.id)) return;

    const savedId = readSavedClientId();
    // Default-client parity with legacy `HeaderService.getSelectedClient()`:
    // its sort `(a, b) => a.id - b.id` runs on GUID *strings* → `NaN` → a
    // no-op, so legacy effectively keeps the API response order and picks
    // `clients[0]`. We were sorting by GUID here, which surfaced a different
    // client (e.g. "frost") instead of the API-first one ("Client 1 New").
    const initial = query.data.find((c) => c.id === savedId) ?? query.data[0];
    // `silent: true` prevents broadcasting to other tabs on first load.
    // Without this, a duplicated tab's initial client selection triggers
    // `cross-tab.ts` in the original tab, which calls `msalInstance.clearCache()`
    // and wipes the original tab's active account ("Not signed in" bug).
    if (initial) setCurrent(initial, { silent: true, source: 'reset' });
  }, [query.data, current, setAvailable, setCurrent]);

  // Bug #4 (legacy parity): an authenticated user with zero accessible clients
  // must not be left inside a client-less shell (empty MFE host). Legacy routed
  // zero-client users to the login page (a SOFT block) rather than a hard MSAL
  // logout, so we mark interaction required + replace to /login (the interaction
  // flag stops LoginPage auto-forwarding, so this cannot loop). This also covers
  // an admin revoking a user's access to every client while active — a SignalR
  // `rumupdatemethod` refreshes the client list (see `portal-message-dispatch.ts`)
  // and an empty result bounces them to /login.
  useEffect(() => {
    if (query.isSuccess) clientFetchFailureHandled = false;
    if (query.isSuccess && query.data.length === 0 && !zeroClientRedirectInitiated) {
      zeroClientRedirectInitiated = true;
      markInteractionRequired();
      getTracker().event('route_guard_blocked', {
        guard: 'requireClient',
        reason: 'no_available_clients',
        redirect_target: '/login',
      });
      const router = getRouterInstance();
      if (router) router.history.replace('/login');
    }
  }, [query.isSuccess, query.data]);

  // Client-list FETCH FAILURE (blocked request / 5xx / network). Parity with
  // legacy `authorize.getClientsFromRum` (error → NAVIGATION.LOGIN_URL) and the
  // BaseRouteGuard/ValidateRouteGuard role-mapping error path (INTERACTION_REQUIRED
  // + /login): an authenticated user whose client list can't be resolved must
  // NOT be stranded in a client-less shell ("Select client", still logged in).
  // This closes the gap on the already-authenticated / hard-refresh path, which
  // never runs FrameRedirectPage's pre-shell client gate. Only bounce when no
  // client is already resolved (a transient blip after a client is active must
  // not eject the user). The interaction flag stops LoginPage auto-forwarding,
  // so this cannot loop.
  useEffect(() => {
    if (!query.isError || clientFetchFailureHandled) return;
    if (useClientStore.getState().current) return;
    clientFetchFailureHandled = true;
    markInteractionRequired();
    getTracker().event('route_guard_blocked', {
      guard: 'requireClient',
      reason: 'client_list_fetch_failed',
      redirect_target: '/login',
    });
    const router = getRouterInstance();
    if (router) router.history.replace('/login');
  }, [query.isError]);

  return query;
}

/**
 * Toggle favourite flag on a client. Optimistic update + invalidation on error.
 * Mirrors `HeaderService.updateFavoriteClient()`.
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const toggleFavorite = useClientStore((s) => s.toggleFavorite);

  return useMutation({
    mutationFn: async ({ id, favorite }: { id: string; favorite: boolean }) => {
      await http(Endpoints.favoriteClient(id), { method: 'POST' });
      return { id, favorite };
    },
    onMutate: ({ id, favorite }) => {
      toggleFavorite(id, favorite);
    },
    onError: (_err, { id, favorite }) => {
      toggleFavorite(id, !favorite);
    },
    onSuccess: (_data, { id, favorite }) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Notify other tabs so their dropdown reflects the change immediately.
      postFavoriteToggle({ clientId: id, favorite });
    },
  });
}
